import React from 'react'
import { Button } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import format from 'date-fns/format'

import getToAccrueDaysAndHours from '../../functions/get-to-accrue-days-and-hours'
import { 
  getPayoutDateForBiWeeklyPeriod,
  getPayoutDateForWeeklyPeriod,
  getPayoutDateForSemiMonthlyPeriod,
  roundTo2Decimals,
  roundTo4Decimals
} from '@vacationtracker/shared/functions/calculation-shared'
import { getPayoutDateForMonthlyPeriod } from '@vacationtracker/shared/functions/calculation-shared'
import { getDatesForAccrualsMonthly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-monthly'
import { getDatesForAccrualsBiWeekly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-bi-weekly'
import { getDateFnsLocale } from '../../i18n'
import { getDatesForAccrualsSemiMonthly } from '../../functions/get-dates-for-accruals/get-dates-for-accruals-semi-monthly'

import { AccrualTypeEnum } from '@vacationtracker/shared/types/leave-policy'
import { IAccruedTooltipProps } from './types'
import { getDatesForAccrualsWeekly } from '../../functions/get-dates-for-accruals/get-dates-for-accruals-weekly'
import { getAverageWorkingHours } from '../../functions/work-week'
import { getRawQuotaDaysOrHours } from '../../functions/quotas'

export const AccruedTooltip = ({
  selectedLeaveType,
  userName,
  workingHours,
  policyFirstEarningDate,
  handleGoToUserLogPage,
  formatMessage,
  IntlMessages,
  yearStartDay,
  accrualPeriodStart,
  capHit,
  isCurrentYearSelected,
  hourlyLeaveAccounting,
  userLocale,
  userStatus = 'ACTIVE',
}: IAccruedTooltipProps) => {
  const startPeriod = accrualPeriodStart
  const today: Date = new Date()
  const toilDays = Number(selectedLeaveType.toilDays - selectedLeaveType.toilDaysLapsed)
  // earnedWithoutRollover no longer includes TOIL (after VT-11582 fix), so we don't need to subtract it
  const earnedDays = selectedLeaveType.quotas.earnedWithoutRollover.raw.days
  const workDay = getAverageWorkingHours(workingHours)
  const earnedHours = roundTo2Decimals(selectedLeaveType.quotas.earnedWithoutRollover.raw.hours)
  let policyNextEarningDate
  const {
    toAccrueDays,
    toAccrueHours,
  } = getToAccrueDaysAndHours(
    hourlyLeaveAccounting,
    selectedLeaveType.accrualType,
    hourlyLeaveAccounting ? selectedLeaveType.quotas.quotaAtYearEnd.raw.hours : selectedLeaveType.quotas.quotaAtYearEnd.raw.days,
    workDay
  )
  // here we have to see if monthly, weekly or bi-weekly
  // TODO: check for the last month of period for rounding
  // show info message when/how much should a user accrue
  // only when current year is selected
  if (isCurrentYearSelected) {
    switch (selectedLeaveType.accrualType) {
      case AccrualTypeEnum.monthly: {
        const { accrualPeriodEnd } = getDatesForAccrualsMonthly({today, yearStartDay})
        policyNextEarningDate = getPayoutDateForMonthlyPeriod(
          accrualPeriodEnd,
          policyFirstEarningDate,
          accrualPeriodStart
        )
        break
      }
      case AccrualTypeEnum.semiMonthly: {
        const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsSemiMonthly({today, yearStartDay})
        const todayString = today.toISOString().split('T')[0]
        policyNextEarningDate = getPayoutDateForSemiMonthlyPeriod({
          periodStart: accrualPeriodStart,
          periodEnd: accrualPeriodEnd,
          firstEarningDate: policyFirstEarningDate,
          accrualPeriodStart: accrualPeriodStart,
          firstPayoutDateOnOrAfterThisDate: todayString,
        })
        break
      }
      case AccrualTypeEnum.biWeekly: {
        const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsBiWeekly({ today, accrualPeriodStart: startPeriod })
        policyNextEarningDate =  getPayoutDateForBiWeeklyPeriod(
          accrualPeriodStart,
          accrualPeriodEnd,
          policyFirstEarningDate,
          accrualPeriodStart ? accrualPeriodStart : undefined
        )
        break
      }
      case AccrualTypeEnum.weekly: {
        const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsWeekly({ today, accrualPeriodStart: startPeriod })
        policyNextEarningDate = getPayoutDateForWeeklyPeriod(
          accrualPeriodStart,
          accrualPeriodEnd,
          policyFirstEarningDate,
          accrualPeriodStart
        )
        break
      }
      default:
        break
    }
  }

  return (<>
    {capHit &&
      <p style={{ fontSize: '16px' }}>
        <ExclamationCircleOutlined style={{ color: 'red' }} />&nbsp;
        <IntlMessages id="components.leavePolicyForm.accrualCapWarning" values={{ leaveTypeName: selectedLeaveType.leaveTypeName }}/>
      </p>
    }
    <p><IntlMessages id="components.userLeaveQuotas.accrualsTooltipTitle" /></p>
    <p>
      <IntlMessages
        id={`components.userLeaveQuotas.accrualsTooltipTitle-${selectedLeaveType.accrualType.toLowerCase()}`}
        values={{ leaveType: selectedLeaveType.leaveTypeName}}
      />
    </p>
    <p>
      {formatMessage({ id: hourlyLeaveAccounting ? 'components.userLeaveQuotas.accruedInfo1Hours' : 'components.userLeaveQuotas.accruedInfo1Days'}, {
        userName,
        earnedDays: roundTo2Decimals(earnedDays),
        earnedDaysInHours: roundTo2Decimals(earnedHours),
        defaultDays: roundTo4Decimals(selectedLeaveType.quotas.quotaAtYearEnd.raw.days),
        defaultDaysInHours: roundTo2Decimals(selectedLeaveType.quotas.quotaAtYearEnd.raw.hours),
      })}
    </p>
    {isCurrentYearSelected && userStatus === 'ACTIVE' && <p>
      {formatMessage({ id: hourlyLeaveAccounting ? 'components.userLeaveQuotas.accruedInfo2Hours' : 'components.userLeaveQuotas.accruedInfo2Days'}, {
        userName,
        toAccrueDays: roundTo4Decimals(toAccrueDays),
        toAccrueHours: roundTo2Decimals(toAccrueHours),
        earnDate: format(new Date(policyNextEarningDate as Date), 'MMM dd, yyyy', {locale: getDateFnsLocale(userLocale)}),
      })}
    </p>}
    {userStatus !== 'ACTIVE' && <p><IntlMessages id="components.userLeaveQuotas.userInactiveInfo" /></p>}
    {toilDays > 0 && <p>
      <IntlMessages id="components.toil.earned"/> <IntlMessages id={hourlyLeaveAccounting ? 'app.hours': 'app.days'}/>:&nbsp;
      { getRawQuotaDaysOrHours(hourlyLeaveAccounting, selectedLeaveType.quotas.toil) }
      {selectedLeaveType.toilDaysLapsed > 0 &&
      <>&nbsp;({ getRawQuotaDaysOrHours(hourlyLeaveAccounting, selectedLeaveType.quotas.toilLapsed) }&nbsp;<IntlMessages id="components.toil.lapsed"/>)</>}
    </p>}
    {toilDays > 0 && <p>
      <IntlMessages
        id="components.toil.accrualsToilsTotal"
        values={
          hourlyLeaveAccounting ? {
            hours: 1,
            total: roundTo2Decimals(selectedLeaveType.quotas.earnedWithoutRollover.raw.hours) + toilDays * workDay,
          } : {
            hours: 2,
            total: roundTo4Decimals(selectedLeaveType.quotas.earnedWithoutRollover.raw.days) + toilDays,
          }
        }
      />
    </p>}
    <p>
      <IntlMessages id="components.userLeaveQuotas.forMoreDetails" /> <Button type='link' onClick={handleGoToUserLogPage}>
        <IntlMessages id="components.userLeaveQuotas.logsTab" />
      </Button>
    </p>
  </>)
}
